import { FormattedMessage, FormattedNumber } from 'react-intl';
import { Fragment, useEffect, useState } from 'react';
import { Locale } from '@synergy/models';
import { PaloAmount, PaloColumn, PaloListView, PaloRowAction } from '@synergy/components-palo';
import { PreviewImage } from '@synergy/components';
import { Theme } from '@mui/material';
import { makeStyles } from '@mui/styles';
import { sortByProperty } from '@synergy/utils';
import { useParams } from 'react-router';

import { ProductVariant } from './models/product-detail-variant';
import { bestLocaleSelector } from '../../../../../common/selectors/locale-selectors';
import { useAppSelector } from '../../../../../store/use-appstate-selector';

const useStyles = makeStyles((theme: Theme) => ({
    starred: {
        display: 'flex',
        gap: '4px',
        alignItems: 'center',
        color: theme.palette.primary.main,

        '& p': {
            color: theme.palette.primary.main,
            lineHeight: 1.5,
        },
    },
    starIcon: {
        width: 17,
        height: 17,
    },
    infoText: {
        color: theme.palette.grey[500],
    },
    defaultLanguage: {
        color: '#979797',
    },
    // HACK: should be done in table component!
    table: {
        '& tr > td': {
            backgroundColor: '#fff',
        },
        '& tr:first-child > td': {
            borderBottomColor: '#95999b',
        },
        '& tr > td:last-child': {
            width: 0,
            paddingRight: 12,
        },
    },
}));

export interface ProductDetailsVariantsTableProps {
    variants: ProductVariant[];
    selectedLanguage: Locale;
    onEdit: (variant: ProductVariant) => void;
    onStatusChange: (variant: ProductVariant) => void;
    onDelete: (variant: ProductVariant) => void;
    onUnlink: (variant: ProductVariant) => void;
}

export const ProductDetailVariantsTable = (props: ProductDetailsVariantsTableProps) => {
    const { variants, selectedLanguage, onDelete, onEdit, onStatusChange, onUnlink } = props;
    const classes = useStyles();
    const params = useParams<{ id?: string }>();
    const [sort, setSort] = useState<{ key: string; order: 'asc' | 'desc' }>({ key: 'name', order: 'asc' });
    const [orderedVariants, setOrderedVariants] = useState<ProductVariant[]>([]);
    const { currency } = useAppSelector(state => state.settings);
    const locale = useAppSelector(state => bestLocaleSelector(state));
    const { currentLocale } = useAppSelector(state => state);

    useEffect(() => {
        if (!params.id || isNaN(+params.id) || variants.length <= 1 || variants?.[0].id === +params.id) {
            setOrderedVariants(variants);
            return;
        }

        const variantId = +params.id;

        const findOwnVariant = variants.find(variant => variant.id === variantId);
        const filteredVariants = variants.filter(variant => variant.id !== variantId);

        if (findOwnVariant) {
            setOrderedVariants([{ ...findOwnVariant }, ...filteredVariants]);
        }
    }, [params.id, variants]);

    const columns: PaloColumn<ProductVariant>[] = [
        {
            key: 'preview',
            type: 'icon',
            head: '',
            cell: variant =>
                variant.previewImage && (
                    <PreviewImage url={variant.previewImage['200x200']} alt={variant.name[selectedLanguage]} />
                ),
        },
        {
            key: 'number',
            type: 'articleNumber',
            sortKey: 'number',
            head: <FormattedMessage id="app.table.column.articleNumber" />,
            cell: variant => variant.number,
        },
        {
            key: 'name',
            type: 'name',
            sortKey: 'name',
            head: <FormattedMessage id="app.table.column.article" />,
            cell: variant =>
                variant.name[selectedLanguage] || (
                    <span className={classes.defaultLanguage}>
                        {currentLocale.toUpperCase()}: {variant.name[currentLocale]}
                    </span>
                ),
        },
        {
            key: 'attributes',
            type: 'name',
            wrap: true,
            head: <FormattedMessage id="app.table.column.attributes" />,
            cell: variant =>
                variant.attributeLabels[locale] ? (
                    variant.attributeLabels[locale]
                        .sort((a, b) => a.localeCompare(b))
                        .map((attribute, index) => {
                            const splitAttribute = attribute.split(' ');
                            const isLastAttribute = variant.attributeLabels[locale].length - 1 === index;

                            return (
                                <Fragment key={index}>
                                    <strong>{splitAttribute[0]}</strong>
                                    &nbsp;
                                    <span>{splitAttribute[1]}</span>
                                    {!isLastAttribute && <span> | </span>}
                                </Fragment>
                            );
                        })
                ) : (
                    <span>&ndash;</span>
                ),
        },
        {
            key: 'currentPrice',
            type: 'price',
            sortKey: 'currentPrice',
            currency,
            head: <FormattedMessage id="app.table.column.currentPrice" />,
            cell: variant =>
                isNaN(variant.currentPrice[currency]) ? (
                    <span>&ndash;</span>
                ) : (
                    <PaloAmount value={variant.currentPrice[currency]} maximumFractionDigits={4} />
                ),
        },
    ];

    // The article being looked at leads the list and offers nothing to do to
    // itself; the mask around it already does that.
    const variantActions = (variant: ProductVariant): PaloRowAction[] =>
        params.id && +params.id !== variant.id
            ? [
                  { label: <FormattedMessage id="app.common.edit" />, onClick: () => onEdit(variant) },
                  {
                      label: variant.isActive ? (
                          <FormattedMessage id="app.common.deactivate" />
                      ) : (
                          <FormattedMessage id="app.common.activate" />
                      ),
                      onClick: () => onStatusChange(variant),
                  },
                  {
                      label: <FormattedMessage id="app.product.detail.variants.unlink" />,
                      onClick: () => onUnlink(variant),
                  },
                  { label: <FormattedMessage id="app.product.detail.delete" />, onClick: () => onDelete(variant) },
              ]
            : [];

    const sortedVariants = sortByProperty({
        data: orderedVariants.slice(1),
        sortDirection: sort.order,
        valueSelector: variant => {
            switch (sort.key) {
                case 'currentPrice':
                    return variant.currentPrice[currency];
                case 'name':
                    return variant.name[locale];

                default:
                    return variant[sort.key as keyof ProductVariant];
            }
        },
    });

    if (orderedVariants.length === 0) {
        return null;
    }

    return (
        <PaloListView<ProductVariant, number>
            embedded={true}
            columns={columns}
            items={[orderedVariants[0], ...sortedVariants]}
            getRowId={variant => variant.id}
            emptyLabel={<FormattedMessage id="app.common.emptyResult" />}
            isRowMuted={variant => !variant.isActive}
            sort={sort}
            onSortChange={(key, order) => setSort({ key, order })}
            rowActions={variantActions}
        />
    );
};
